import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetOrganizationUnitsDocument,
  CreateOrganizationUnitDocument,
  type GetOrganizationUnitsQuery,
  type CreateOrganizationUnitMutation,
  type CreateOrganizationUnitMutationVariables,
} from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { useNotification } from '@/app/contexts/NotificationContext';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
interface UnitRow extends Record<string, unknown> {
  id: string;
  name: string;
  description?: string | null;
  orgName: string;
  tenantName: string;
  sort: number;
  branchType: string | null;
  appType: string | null;
  createdAt: string;
  updatedAt: string;
}

export default function OrganizationUnits() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDateTime = (iso: string) => new Date(iso).toLocaleString(locale);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get('tenantId') ?? undefined;
  const organizationId = searchParams.get('organizationId') ?? undefined;
  const { showSuccess, showError } = useNotification();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newBranchType, setNewBranchType] = useState('');
  const [newAppType, setNewAppType] = useState('');

  useEffect(() => {
    if (tenantId) setOverrideTenantId(tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [tenantId]);

  const { data, loading, refetch } = useQuery<GetOrganizationUnitsQuery>(
    GetOrganizationUnitsDocument,
    {
      variables: {
        first: GRAPHQL_MAX_PAGE_SIZE,
        organizationId: organizationId ?? null,
      },
    },
  );

  const [createUnit, { loading: creating }] = useMutation<
    CreateOrganizationUnitMutation,
    CreateOrganizationUnitMutationVariables
  >(CreateOrganizationUnitDocument);

  const nodes = data?.organizationUnits?.nodes ?? [];

  const rows: UnitRow[] = nodes.map((n) => ({
    id: n.id,
    name: n.name,
    description: n.description,
    orgName: n.organization?.name ?? '–',
    tenantName: n.tenant?.name ?? '–',
    sort: n.sort,
    branchType: n.branchType ?? null,
    appType: n.appType ?? null,
    createdAt: n.createdAt,
    updatedAt: n.updatedAt,
  }));

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('common.name'), flex: 1, minWidth: 160 },
    { field: 'orgName', headerName: t('organizationDetail.customer'), flex: 1, minWidth: 140 },
    { field: 'tenantName', headerName: t('common.tenant'), flex: 1, minWidth: 120 },
    { field: 'description', headerName: t('common.description'), flex: 1, minWidth: 140 },
    {
      field: 'branchType',
      headerName: t('orgUnits.branchType'),
      width: 120,
      valueFormatter: (v: string | null) => v || '–',
    },
    {
      field: 'appType',
      headerName: t('orgUnits.appType'),
      width: 110,
      valueFormatter: (v: string | null) => v || '–',
    },
    { field: 'sort', headerName: t('orgUnits.sort'), width: 80 },
    {
      field: 'createdAt',
      headerName: t('common.created'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
    {
      field: 'updatedAt',
      headerName: t('common.updated'),
      width: 150,
      valueFormatter: (v: string) => formatDateTime(v),
    },
  ];

  const handleCreate = async () => {
    if (!organizationId || !newName.trim()) {
      showError(t('orgUnits.createNeedOrg'));
      return;
    }
    try {
      const res = await createUnit({
        variables: {
          input: {
            organizationId,
            name: newName.trim(),
            description: newDescription.trim() || undefined,
            branchType: newBranchType.trim() || undefined,
            appType: newAppType.trim() || undefined,
          },
        },
      });
      const top = (res as { errors?: ReadonlyArray<{ message?: string }> }).errors;
      if (top?.length) {
        showError(top.map((e) => e.message).join(' '));
        return;
      }
      if (!res.data?.createOrganizationUnit?.organizationUnit) {
        showError(t('errors.generic'));
        return;
      }
      showSuccess(t('orgUnits.created'));
      setCreateOpen(false);
      setNewName('');
      setNewDescription('');
      setNewBranchType('');
      setNewAppType('');
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="h4">{t('orgUnits.title')}</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)} disabled={!organizationId}>
          {t('orgUnits.create')}
        </Button>
      </Box>

      <DataTable<UnitRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
        onRowClick={(row) => navigate(`/organization-units/${row.id}`)}
      />

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('orgUnits.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.name')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label={t('common.description')}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label={t('orgUnits.branchType')}
            value={newBranchType}
            onChange={(e) => setNewBranchType(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('orgUnits.appType')}
            value={newAppType}
            onChange={(e) => setNewAppType(e.target.value)}
            fullWidth
          />
          {!organizationId && (
            <Typography color="error" variant="body2">
              {t('orgUnits.createNeedOrg')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={creating || !organizationId} variant="contained">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
